import { useState } from 'react';
import { Info, LogOut, Crown, Settings, Headset, Sparkles, Moon, Sun, Palette, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  // DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from 'components/ui/dropdown-menu';
import { useSignoutMutation } from 'features/auth/hooks/use-auth';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useNavigate, useLocation } from 'react-router-dom';
import DummyProfile from 'assets/images/dummy_profile.png';
import { UProfileItem } from '/pages/main/uprofile/types/uprofile-type';
import { ArchiveChat } from 'pages/archive/archive-chat';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useQueryClient } from "@tanstack/react-query";
import { useSubscriptionStore } from 'state/store/subscription/useSubscriptionStore';
import { CustomDialog } from '../custom-dialog';
import { SettingsDialog } from '../settings-dialog';
import BeBraveLogo from 'assets/images/bebrave-logo.png';
import { KnowledgeBaseModal } from 'features/profile/components/knowledge-base-modal';
import packageJson from '../../../../package.json';
import { PLANS } from 'constant/plan';
import ContactModal from 'pages/subscription/contact-sales-modal';
import { useContactSales } from 'pages/subscription/use-subscription';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { useSecureConfigStore } from 'state/store/config/useConfigStore';
import { useTheme } from 'components/core/theme-provider';
import { updateCurrentLanguage } from '../language-selector/services/language-service';

/**
 * UProfileMenu Component
 *
 * A user profile dropdown menu component that displays user information and provides
 * navigation and account management options.
 *
 * Features:
 * - Displays user profile image and name
 * - Shows loading states with skeleton placeholders
 * - Provides navigation to profile page
 * - Includes theme toggling functionality
 * - Handles user logout with authentication state management
 * - Responsive design with different spacing for mobile and desktop
 *
 * Dependencies:
 * - Requires useTheme hook for theme management
 * - Requires useAuthStore for authentication state management
 * - Requires useSignoutMutation for API logout functionality
 * - Requires useGetAccount for fetching user account data
 * - Uses DropdownMenu components for the menu interface
 * - Uses React Router's useNavigate for navigation
 *
 * @returns {JSX.Element} A dropdown menu component for user profile management
 *
 * @example
 * // Basic usage in a header or navigation component
 * <UProfileMenu />
 */

export const UProfileMenu = ({ isDropdownIconRequired, isFullNameRequired }: UProfileItem) => {
  console.log('isDropdownIconRequired', isDropdownIconRequired, 'isFullNameRequired', isFullNameRequired)
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [isKnowledgeBaseModalOpen, setIsKnowledgeBaseModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { theme, setTheme } = useTheme();

  // Contact sales integration
  const { mutateAsync: submitContactForm } = useContactSales();
  const { configurationSettings } = useConfig();
  const { currentLanguage, setLanguage, availableLanguages } = useLanguageContext();
  const { siteConfigurationSettings } = useSecureConfigStore();

  // Check if subscription info should be shown (only show if explicitly true)
  const showSubscriptionInfo = siteConfigurationSettings?.siteConfigurations?.data?.showSubscriptionInfo === true;

  const { logout } = useAuthStore();
  const { mutateAsync } = useSignoutMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const user = useCommonStore((state) => state.user);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const isOnlyOrgUser = useCommonStore((state) => state.isOnlyOrgUser);
  const currentPlan = useSubscriptionStore((state) => state.currentPlan);

  const signoutHandler = async () => {
    try {
      const res = await mutateAsync();
      if (res.isSuccess) {
        logout();
        queryClient.clear();
        navigate('/login');
      }
    } catch (_error) {
      /* empty */
    }
  };

  const handleContactSubmit = async (data: any) => {
    const payload = {
      language: currentLanguage,
      message: data?.message,
      organizationName: data?.company,
      contactName: data?.name,
      phone: data?.phone,
      emailTo: configurationSettings.paths.salesContactMailTo,
      email: data?.email,
    };

    return await submitContactForm(payload);
  };

  const changeLanguage = async (newLanguageCode: string) => {
    if (user) {
      updateCurrentLanguage(user.id, newLanguageCode, configurationSettings);
    }
    await setLanguage(newLanguageCode);
  };

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';
  const role = isAdmin ? t('ADMIN') : isOrgAdmin ? t('ORG_ADMIN') : t('USER');

  return (
    <div>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <div className="cursor-pointer rounded-full hover:bg-accent transition-colors">
            <div className="relative w-8 h-8">
              <img
                src={user?.profileImageUrl || DummyProfile}
                alt="profile"
                loading="lazy"
                className="object-cover w-8 h-8 rounded-full"
              />

              {currentPlan && currentPlan.name && currentPlan.name !== PLANS.NOPLAN && currentPlan.name !== PLANS.FREE && (
                <div className="absolute -bottom-0.5 -right-0.5 bg-background border border-border rounded-full p-0.5">
                  <Crown className="h-2 w-2 text-primary fill-primary" />
                </div>
              )}
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="flex flex-col w-[241.6px] pt-3 pb-[14px] px-0 bg-background border-[0.5px] rounded-[8px] shadow-primary-soft"
          align="end"
          side="top"
          sideOffset={10}
        >
          {/* Profile Header */}
          <div className="px-4 pb-4 border-b border-border">
            <div className="text-[12px] leading-[18px] font-normal word-break break-words truncate">
              {user?.email}
            </div>
            <div className="text-[14px] leading-[22px] font-normal">
              {role}
            </div>
          </div>

          {/* Group 1: Settings, Language, Appearance */}
          <div className="flex flex-col mt-[14px] mx-2 pb-[14px] border-b-[0.5px]">
            <DropdownMenuItem
              className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors group"
              onClick={() => setIsSettingsDialogOpen(true)}
            >
              <Settings className="h-4 w-4 text-foreground" />
              <span className="text-[14px] leading-[22px] font-normal flex-1">{t('SETTINGS') || 'Settings'}</span>
            </DropdownMenuItem>

            {/* Language Sub-menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors">
                <Globe className="h-4 w-4 text-foreground" />
                <span className="text-[14px] leading-[22px] font-normal flex-1">{t('LANGUAGE') || 'Language'}: <span className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'} font-medium ml-1`}>{currentLanguage.split('-')[0].toUpperCase()}</span></span>
                {/* <ChevronRight className="h-4 w-4 text-foreground" /> */}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-background border border-border p-1.5 rounded-[12px] min-w-[140px] shadow-xl" sideOffset={12}>
                {availableLanguages?.map((lang) => (
                  <DropdownMenuItem
                    key={lang.itemId}
                    className={`px-3 py-[6px] cursor-pointer rounded-[6px] focus:bg-white/5 ${lang.languageCode === currentLanguage ? 'text-primary-600' : 'text-white/70'}`}
                    onClick={() => changeLanguage(lang.languageCode)}
                  >
                    {lang.languageName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Appearance Sub-menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="bg-background hover:bg-accent flex items-center px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors">
                <Palette className="h-4 w-4 text-foreground mr-4" />
                <span className="text-[14px] leading-[22px] font-normal">
                  {t('APPEARANCE') || 'Appearance'}: <span className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'} font-medium mr-1 capitalize truncate`}>
                    {theme === 'dark' ? t('DARK') : t('LIGHT')}
                  </span>
                </span>
                {theme === 'dark' ? <Moon className="h-4 w-4 text-primary-200" /> : <Sun className="h-4 w-4 text-primary-600" />}
                {/* <ChevronRight className="h-4 w-4 text-foreground" /> */}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-background border border-border p-1.5 rounded-[12px] min-w-[140px] shadow-xl" sideOffset={12}>
                <DropdownMenuItem
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer rounded-[6px] focus:bg-white/5 ${theme === 'dark' ? 'text-primary-600' : 'text-typography-grey-1-secondary'}`}
                  onClick={() => setTheme('dark')}
                >
                  <span>{t('DARK') || 'Dark'}</span>
                  <Moon className="h-4 w-4" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer rounded-[6px] focus:bg-white/5 ${theme === 'light' ? 'text-primary-600' : 'text-typography-white'}`}
                  onClick={() => setTheme('light')}
                >
                  <span>{t('LIGHT') || 'Light'}</span>
                  <Sun className="h-4 w-4" />
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </div>

          {/* <DropdownMenuSeparator className="bg-[#262626] mx-[-8px] my-1.5" /> */}

          {/* Group 2: Change Plan */}
          {!isOnlyOrgUser && showSubscriptionInfo && (
            <DropdownMenuItem
              className="bg-background hover:bg-accent flex items-center gap-4 mx-2 my-[14px] px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors group"
              onClick={() => {
                // Get current route with search params to return to later
                const currentRoute = `${location.pathname}${location.search}`;
                // Encode the current route to safely pass as URL parameter
                const encodedRoute = encodeURIComponent(currentRoute);
                const baseTab = isOrgAdmin ? 'business' : 'personal';
                const targetUrl = `/subscription?tab=${baseTab}&from=${encodedRoute}`;
                navigate(targetUrl);
                setIsDropdownOpen(false);
              }}
            >
              <Sparkles className="h-4 w-4 text-foreground" />
              <span className="text-[14px] leading-[22px] font-normal flex-1">{t('CHANGE_PLAN') || 'Change Plan'}</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator className=" mx-4 my-0" />

          {/* Group 3: Contact Us, About */}
          <div className='mx-2 my-[14px]'>
            <DropdownMenuItem
              className="bg-background hover:bg-accent flex items-center gap-4 p2-3 py-[6px] cursor-pointer rounded-[8px] transition-colors group"
              onClick={() => setIsContactModalOpen(true)}
            >
              <Headset className="h-4 w-4 text-foreground" />
              <span className="text-[14px] font-normal flex-1">{t('CONTACT_US') || 'Contact Us'}</span>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="bg-background hover:bg-accent flex items-center gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors group"
              onClick={() => setIsAboutDialogOpen(true)}
            >
              <Info className="h-4 w-4 text-foreground" />
              <span className="text-[14px] font-normal flex-1">{t('ABOUT') || 'About'}</span>
            </DropdownMenuItem>
          </div>

          <DropdownMenuSeparator className=" mx-4 my-0" />

          {/* Group 4: Log out */}
          <DropdownMenuItem
            className="bg-background hover:bg-accent flex items-center mx-2 mt-[14px] gap-4 px-2 py-[6px] cursor-pointer rounded-[8px] transition-colors group"
            onClick={signoutHandler}
          >
            <LogOut className="h-4 w-4 text-foreground" />
            <span className="text-[14px] font-normal flex-1">{t('LOG_OUT') || 'Log out'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>

      </DropdownMenu>
      {isModalOpen && <ArchiveChat isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />}
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseModalOpen}
        onClose={() => setIsKnowledgeBaseModalOpen(false)}
      />
      <CustomDialog
        isOpen={isAboutDialogOpen}
        setIsOpen={setIsAboutDialogOpen}
        title={t('ABOUT')}
        description=""
        cancelText={t('CLOSE')}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          <img src={BeBraveLogo} alt="BeBrave Logo" className="w-16 h-16" />
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-1">EagleGPT</h3>
            <p className="text-sm text-medium-emphasis">Version {packageJson.version}</p>
          </div>
        </div>
      </CustomDialog>
      <SettingsDialog
        isOpen={isSettingsDialogOpen}
        setIsOpen={setIsSettingsDialogOpen}
        currentPlan={currentPlan}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSubmit={handleContactSubmit}
        title={t('CONTACT_US')}
        initialData={{
          name: fullName,
          email: user?.email || "",
          phone: user?.phoneNumber || "",
        }}
      />
    </div >
  );
};
